import React, { useState } from "react";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
} from "firebase/firestore";
import { db, storage } from "../../../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import LocationPicker from "./LocationPicker";
import ngeohash from "ngeohash";

const PublicTimeCapsuleForm = ({ onClose, refreshList, initialData }) => {
  const isEdit = Boolean(initialData);

  // State for inputs
  const [messageTitle, setMessageTitle] = useState(initialData?.messageTitle || "");
  const [messageBody, setMessageBody] = useState(initialData?.messageBody || "");

  // Date-only fields (YYYY-MM-DD)
  const [revealDate, setRevealDate] = useState(
    initialData?.revealDate
      ? new Date(initialData.revealDate.seconds * 1000).toISOString().substring(0, 10)
      : new Date().toISOString().substring(0, 10)
  );
  const [endDate, setEndDate] = useState(
    initialData?.endDate
      ? new Date(initialData.endDate.seconds * 1000).toISOString().substring(0, 10)
      : ""
  );

  const [status, setStatus] = useState(initialData?.status || "draft");
  const [capsuleType, setCapsuleType] = useState(initialData?.capsuleType || "public");
  const [mediaFile, setMediaFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState({});

  const [location, setLocation] = useState(initialData?.location || null);
  const [openLocationPicker, setOpenLocationPicker] = useState(false);

  const validateForm = () => {
    let newErrors = {};
    if (!messageTitle.trim()) newErrors.messageTitle = "Title is required.";
    if (!messageBody.trim()) newErrors.messageBody = "Message Body is required.";
    if (!capsuleType) newErrors.capsuleType = "Capsule Type is required.";
    if (!status) newErrors.status = "Status is required.";
    if (!location || !location.address) newErrors.location = "Location is required. Please select a location.";
    if (endDate && new Date(endDate) < new Date(revealDate)) {
      newErrors.endDate = "End date cannot be before reveal date.";
    }
    return newErrors;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowed = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
      if (!allowed.includes(file.type.toLowerCase())) {
        setErrors(prev => ({ ...prev, mediaFile: "Only PNG, JPG, and WEBP formats allowed." }));
        setMediaFile(null);
      } else {
        setErrors(prev => ({ ...prev, mediaFile: undefined }));
        setMediaFile(file);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }

    let mediaUrl = initialData?.media?.[0]?.url || "";
    if (mediaFile) {
      try {
        setUploading(true);
        const storageRef = ref(storage, `timecapsules/${Date.now()}-${mediaFile.name}`);
        await uploadBytes(storageRef, mediaFile);
        mediaUrl = await getDownloadURL(storageRef);
      } catch (err) {
        console.error("Error uploading media:", err);
      }
      setUploading(false);
    }

    const geoHashValue = location ? ngeohash.encode(location.lat, location.lng, 9) : null;

    const docData = {
      messageTitle,
      messageBody,
      revealDate: Timestamp.fromDate(new Date(revealDate)),
      status,
      capsuleType,
      location: location ? { lat: location.lat, lng: location.lng, address: location.address } : null,
      geohash: geoHashValue,
      media: mediaUrl ? [{
        type: mediaFile ? (mediaFile.type.startsWith("video") ? "video" : "image") : initialData?.media?.[0]?.type,
        url: mediaUrl,
      }] : [],
      createdAt: isEdit ? initialData.createdAt : Timestamp.now(),
    };
    if (endDate) docData.endDate = Timestamp.fromDate(new Date(endDate));

    try {
      if (isEdit) await updateDoc(doc(db, "timeCapsules", initialData.id), docData);
      else await addDoc(collection(db, "timeCapsules"), docData);
      refreshList();
      onClose();
    } catch (err) {
      console.error("Error saving time capsule:", err);
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this time capsule?")) {
      await deleteDoc(doc(db, "timeCapsules", initialData.id));
      refreshList();
      onClose();
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <TextField
        label="Title"
        value={messageTitle}
        onChange={e => setMessageTitle(e.target.value)}
        error={Boolean(errors.messageTitle)}
        helperText={errors.messageTitle}
        fullWidth
        margin="normal"
        inputProps={{ maxLength: 70 }}
      />

      <TextField
        label="Message Body"
        value={messageBody}
        onChange={e => setMessageBody(e.target.value)}
        error={Boolean(errors.messageBody)}
        helperText={errors.messageBody}
        fullWidth
        multiline
        rows={3}
        margin="normal"
        inputProps={{ maxLength: 280 }}
      />

      <TextField
        label="Reveal Date"
        type="date"
        value={revealDate}
        onChange={e => setRevealDate(e.target.value)}
        fullWidth
        margin="normal"
        InputLabelProps={{ shrink: true }}
      />
      {errors.revealDate && <Typography variant="caption" color="error">{errors.revealDate}</Typography>}

      <TextField
        label="End Date (optional)"
        type="date"
        value={endDate}
        onChange={e => setEndDate(e.target.value)}
        fullWidth
        margin="normal"
        InputLabelProps={{ shrink: true }}
        error={Boolean(errors.endDate)}
        helperText={errors.endDate}
      />

      <FormControl fullWidth margin="normal" error={Boolean(errors.capsuleType)}>
        <InputLabel>Capsule Type</InputLabel>
        <Select
          value={capsuleType}
          label="Capsule Type"
          onChange={e => setCapsuleType(e.target.value)}
        >
          <MenuItem value="public">Public (Pin)</MenuItem>
          <MenuItem value="sponsored">Sponsored (Custom ad icon)</MenuItem>
          <MenuItem value="memberExclusive">Member Exclusive (Treasure box icon)</MenuItem>
        </Select>
        {errors.capsuleType && <Typography variant="caption" color="error">{errors.capsuleType}</Typography>}
      </FormControl>

      {/* Clickable Location Field */}
      <Box sx={{ mt: 2 }}>
        <TextField
          label="Location"
          value={location ? location.address : ""}
          error={Boolean(errors.location)}
          helperText={errors.location || "Click to select a location"}
          fullWidth
          margin="normal"
          InputProps={{ readOnly: true }}
          onClick={() => setOpenLocationPicker(true)}
          sx={{ cursor: 'pointer' }}
        />
      </Box>

      <FormControl fullWidth margin="normal">
        <InputLabel>Status</InputLabel>
        <Select
          value={status}
          label="Status"
          onChange={e => setStatus(e.target.value)}
        >
          <MenuItem value="draft">Draft</MenuItem>
          <MenuItem value="published">Published</MenuItem>
        </Select>
      </FormControl>

      <Box sx={{ mt: 2 }}>
        <Button
          variant="contained"
          component="label"
          sx={(theme) => ({
            pl: 2,
            pr: 2,
            backgroundColor: theme.colors.blue,
            border: `1px solid ${theme.colors.blue}`,
            color: theme.colors.white,
            mb: 2,
            textTransform: "none",
            borderRadius: "50px",
            boxShadow: "none",
            mr: 2,
            "&:hover": { boxShadow: "none" },
          })}
        >
          Upload Media
          <input type="file" hidden onChange={handleFileChange} />
        </Button>
        {mediaFile && <span style={{ marginLeft: 10 }}>{mediaFile.name}</span>}
        {errors.mediaFile && (<Typography variant="caption" color="error" sx={{ display: "block", mt: 1, ml: 2 }}>{errors.mediaFile}</Typography>)}
      </Box>

      <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
        <Button onClick={onClose} sx={(theme) => ({ pl: 2, pr: 2, backgroundColor: theme.colors.darkGray, border: `1px solid ${theme.colors.darkGray}`, color: theme.colors.white, mr: 2, textTransform: "none", borderRadius: "50px", "&:hover": { boxShadow: "none" } })}>
          Cancel
        </Button>
        {isEdit && (<Button onClick={handleDelete} sx={(theme) => ({ pl: 2, pr: 2, backgroundColor: theme.colors.red, border: `1px solid ${theme.colors.red}`, color: theme.colors.white, mr: 2, textTransform: "none", borderRadius: "50px", "&:hover": { boxShadow: "none" } })}>
          Delete
        </Button>)}
        <Button type="submit" variant="contained" disabled={uploading} sx={(theme) => ({ pl: 2, pr: 2, backgroundColor: theme.colors.green, border: `1px solid ${theme.colors.green}`, color: theme.colors.white, textTransform: "none", borderRadius: "50px", boxShadow: "none", "&:hover": { boxShadow: "none" } })}>
          {uploading ? "Uploading..." : isEdit ? "Update" : "Submit"}
        </Button>
      </Box>

      <Dialog open={openLocationPicker} onClose={() => setOpenLocationPicker(false)} maxWidth="md" fullWidth>
        <DialogTitle>Select Location</DialogTitle>
        <DialogContent>
          <LocationPicker
            initialLocation={location}
            onLocationSelect={(loc) => {
              setLocation(loc);
              setOpenLocationPicker(false);
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenLocationPicker(false)} sx={(theme) => ({ backgroundColor: theme.colors.blue, border: `1px solid ${theme.colors.blue}`, color: theme.colors.white, textTransform: "none", borderRadius: "50px", "&:hover": { boxShadow: "none" } })}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PublicTimeCapsuleForm;
